import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ClipboardCheck,
  Download,
  Mail,
  Merge,
  Paperclip,
  Pencil,
  Phone,
  Plus,
  Printer,
} from "lucide-react";
import { api, DOC_LABELS, formatApiErrorDetail, money, openDownload } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Label } from "@/components/ui/label";
import { formatPhone } from "@/lib/phone";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CollectKycDialog } from "@/components/CollectKycDialog";

const METHODS = ["CASH", "BANK TRANSFER", "PAYID", "CARD DEBIT", "CARD", "KUBER PAYID", "KUBER INSTALLMENTS", "AFTERPAY", "KLARNA", "ZIP PAY"];

export default function CustomerProfile() {
  const { phone } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entry, setEntry] = useState({
    kind: "deposit",
    amount: "",
    method: "CASH",
    reference: "",
    note: "",
    doc_id: "",
    receipt_file_id: "",
  });
  const [receiptName, setReceiptName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState({ name: "", address: "", email: "", birthday: "", notes: "" });
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeOther, setMergeOther] = useState("");
  const [mergeMatch, setMergeMatch] = useState(null);
  const [mergeReason, setMergeReason] = useState("Duplicate from a mistyped number");
  const [collectOpen, setCollectOpen] = useState(false);

  const load = useCallback(() => {
    api
      .get(`/customers/${phone}`)
      .then(({ data }) => {
        setLoadError(false);
        setData(data);
        setProfile({
          name: data.customer.name || "",
          address: data.customer.address || "",
          birthday: data.customer.birthday || "",
          email: data.customer.email || "",
          notes: data.customer.notes || "",
        });
      })
      .catch(() => setLoadError(true));
  }, [phone]);

  useEffect(load, [load]);

  const uploadReceipt = async (file) => {
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEntry((e) => ({ ...e, receipt_file_id: data.file_id }));
      setReceiptName(data.filename);
      toast.success("Payment proof attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const saveEntry = async () => {
    if (!(Number(entry.amount) > 0)) {
      toast.error("Enter an amount greater than zero");
      return;
    }
    setSaving(true);
    try {
      const { data: created } = await api.post(`/customers/${phone}/entries`, {
        ...entry,
        amount: Number(entry.amount),
      });
      toast.success(entry.kind === "refund" ? "Refund recorded" : "Payment recorded");
      setEntryOpen(false);
      setEntry({
        kind: "deposit",
        amount: "",
        method: "CASH",
        reference: "",
        note: "",
        doc_id: "",
        receipt_file_id: "",
      });
      setReceiptName("");
      load();
      openDownload(`/ledger/${created.id}/receipt`).catch(() => toast.error("Could not open that"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save that");
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async () => {
    if (!profile.name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/customers/${phone}`, profile);
      toast.success("Customer details updated — phone and history kept");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const changePhone = async () => {
    const entered = window.prompt(
      "New mobile number for this customer.\n\n" +
        "Their whole history moves with them — invoices, repairs, KYC and any credit " +
        "sitting on the account.",
      customer.phone
    );
    if (entered === null) return;
    const reason = window.prompt("Why is the number changing? (kept on the record)", "");
    if (!reason) return toast.error("A reason is needed to change the number");
    try {
      const { data: res } = await api.put(`/customers/${phone}/phone`, {
        new_phone: entered,
        reason,
      });
      toast.success("Number changed — their history and credit moved across");
      navigate(`/customers/${res.phone}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not change the number"
      );
    }
  };

  const emailStatement = async () => {
    try {
      const { data } = await api.post(`/customers/${phone}/statement/email`);
      toast.success(`Statement emailed to ${data.sent_to}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not email");
    }
  };

  const applyPending = async () => {
    try {
      await api.post(`/customers/${phone}/pending/apply`);
      toast.success("Customer details updated — history kept");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update");
    }
  };

  const dismissPending = async () => {
    try {
      await api.post(`/customers/${phone}/pending/dismiss`);
      toast.success("Kept the record as it was");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not dismiss");
    }
  };

  const nd = (s) => String(s || "").replace(/\D/g, "");

  const openMerge = () => {
    setMergeOther("");
    setMergeMatch(null);
    setMergeReason("Duplicate from a mistyped number");
    setMergeOpen(true);
  };

  const findOther = async () => {
    if (nd(mergeOther).length < 3) return toast.error("Enter the other phone number");
    if (nd(mergeOther) === nd(customer.phone))
      return toast.error("That is the same customer");
    try {
      const { data } = await api.get("/customers/lookup", { params: { q: mergeOther } });
      const match =
        data.find((r) => nd(r.customer_phone) === nd(mergeOther)) ||
        data.find((r) => nd(r.customer_phone).endsWith(nd(mergeOther).slice(-8))) ||
        null;
      if (!match) return toast.error("No customer found on that number");
      if (nd(match.customer_phone) === nd(customer.phone))
        return toast.error("That is the same customer");
      setMergeMatch(match);
    } catch (err) {
      toast.error("Could not look that up");
    }
  };

  const doMerge = async () => {
    if (!mergeMatch) return;
    if (!mergeReason.trim()) return toast.error("Give a reason for the merge");
    setSaving(true);
    try {
      await api.post(`/customers/${phone}/merge`, {
        from_phone: mergeMatch.customer_phone,
        reason: mergeReason.trim(),
      });
      toast.success("Records merged — their history moved across");
      setMergeOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not merge");
    } finally {
      setSaving(false);
    }
  };

  if (loadError)
    return (
      <div className="p-6" data-testid="customer-profile-error">
        <p className="text-sm text-muted-foreground">
          This customer is not available. They may belong to another store or no longer exist.
        </p>
        <button
          type="button"
          onClick={() => navigate("/customers")}
          className="mt-3 text-sm font-semibold text-[#C5A059] hover:underline"
          data-testid="customer-profile-back"
        >
          ← Back to customers
        </button>
      </div>
    );
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const { customer, rows, documents, totals } = data;
  const owing = totals.outstanding;
  const openInvoices = documents.filter((d) => (d.balance || 0) > 0.005);

  return (
    <div className="fade-up" data-testid="customer-profile-page">
      <button
        onClick={() => navigate("/customers")}
        className="mb-5 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        data-testid="back-to-customers"
      >
        <ArrowLeft className="h-4 w-4" /> All customers
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl" data-testid="customer-name">
            {customer.name || "—"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {formatPhone(customer.phone)}
            {customer.email ? ` · ${customer.email}` : ""}
            {customer.address ? ` · ${customer.address}` : ""}
          </p>
          {customer.former_names?.length > 0 && (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="former-names">
              Formerly {customer.former_names.join(", ")}
            </p>
          )}
          {customer.former_phones?.length > 0 && (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="former-phones">
              Previously on {customer.former_phones.map((p) => formatPhone(p.phone)).join(", ")}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => setEditOpen(true)}
            data-testid="edit-customer-button"
          >
            <Pencil className="mr-1.5 h-4 w-4" /> Edit / rename
          </Button>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={changePhone}
            data-testid="change-phone-button"
          >
            <Phone className="mr-1.5 h-4 w-4" /> Change number
          </Button>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={openMerge}
            data-testid="merge-customer-button"
          >
            <Merge className="mr-1.5 h-4 w-4" /> Merge duplicate
          </Button>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => openDownload(`/customers/${phone}/statement`).catch(() => toast.error("Could not open that"))}
            data-testid="print-statement-button"
          >
            <Printer className="mr-1.5 h-4 w-4" /> Statement
          </Button>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={emailStatement}
            data-testid="email-statement-button"
          >
            <Mail className="mr-1.5 h-4 w-4" /> Email statement
          </Button>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => setCollectOpen(true)}
            data-testid="collect-kyc-button"
          >
            <ClipboardCheck className="mr-1.5 h-4 w-4" /> Collect KYC
          </Button>
          <Button
            className="rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
            onClick={() => setEntryOpen(true)}
            data-testid="take-payment-button"
          >
            <Plus className="mr-1.5 h-4 w-4" /> Take deposit / payment
          </Button>
        </div>
      </div>

      {customer.pending_update?.fields &&
        Object.keys(customer.pending_update.fields).length > 0 && (
          <div
            className="mt-6 border border-[#C5A059] bg-[#FBF8F2] p-4"
            data-testid="pending-update-banner"
          >
            <p className="text-[11px] font-semibold uppercase tracking-widest text-[#8A6A22]">
              This customer entered different details in the queue
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              We never change a record from the public screen. Review and choose to update
              or keep what we have.
            </p>
            <div className="mt-3 space-y-1 text-sm">
              {Object.entries(customer.pending_update.fields).map(([k, ch]) => (
                <div key={k} data-testid={`pending-field-${k}`}>
                  <span className="font-semibold capitalize">{k}:</span>{" "}
                  <span className="text-muted-foreground line-through">{ch.old || "—"}</span>{" "}
                  → <span className="font-semibold">{ch.new}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Button
                className="rounded-none bg-[#0A0A0A] font-semibold text-white hover:bg-[#242424]"
                onClick={applyPending}
                data-testid="apply-pending"
              >
                Update record
              </Button>
              <Button
                variant="outline"
                className="rounded-none"
                onClick={dismissPending}
                data-testid="dismiss-pending"
              >
                Keep as is
              </Button>
            </div>
          </div>
        )}

      <div className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Total invoiced" value={money(totals.invoiced)} testid="stat-invoiced" />
        <Stat label="Total paid" value={money(totals.paid)} testid="stat-paid" />
        <Stat
          label="Credit on account"
          value={money(totals.credit)}
          testid="stat-credit"
          tone={totals.credit > 0 ? "credit" : ""}
        />
        <Stat
          label={owing >= 0 ? "Balance owing" : "In credit"}
          value={money(Math.abs(owing))}
          testid="stat-balance"
          tone={owing > 0.005 ? "owing" : "credit"}
        />
      </div>

      {totals.credit > 0 && (
        <div
          className="mt-4 border border-[#166534]/40 bg-[#F0FDF4] px-4 py-2.5 text-sm"
          data-testid="credit-note"
        >
          This customer has <strong>{money(totals.credit)}</strong> in credit. Enter it as a
          deposit on their next invoice.
        </div>
      )}

      <h2 className="mt-9 text-base font-bold uppercase tracking-wider md:text-lg">
        Account statement
      </h2>
      <div className="mt-3 overflow-x-auto border border-border">
        <table className="w-full min-w-[820px] text-sm" data-testid="statement-table">
          <thead className="bg-[#F8F9FA] text-left">
            <tr className="border-b border-border">
              <th className="px-3 py-2.5 font-semibold">Date</th>
              <th className="px-3 py-2.5 font-semibold">Details</th>
              <th className="px-3 py-2.5 font-semibold">Reference</th>
              <th className="px-3 py-2.5 font-semibold">Staff</th>
              <th className="px-3 py-2.5 text-right font-semibold">Charges</th>
              <th className="px-3 py-2.5 text-right font-semibold">Paid</th>
              <th className="px-3 py-2.5 text-right font-semibold">Balance</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">
                  Nothing on this account yet.
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-[#FBF8F2]" data-testid={`statement-row-${i}`}>
                <td className="px-3 py-2.5 whitespace-nowrap">{r.date}</td>
                <td className="px-3 py-2.5">
                  {r.details}
                  {r.note && <div className="text-xs text-muted-foreground">{r.note}</div>}
                </td>
                <td className="px-3 py-2.5">{r.reference || "—"}</td>
                <td className="px-3 py-2.5">{r.staff || "—"}</td>
                <td className="tabular px-3 py-2.5 text-right">
                  {r.debit ? money(r.debit) : "—"}
                </td>
                <td className="tabular px-3 py-2.5 text-right">
                  {r.credit ? money(r.credit) : "—"}
                </td>
                <td className="tabular px-3 py-2.5 text-right font-semibold">
                  {r.balance < 0 ? (
                    <span className="text-[#166534]">{money(-r.balance)} cr</span>
                  ) : (
                    money(r.balance)
                  )}
                </td>
                <td className="px-3 py-2.5 text-right">
                  {r.doc_id && (
                    <button
                      onClick={() => navigate(`/documents/${r.doc_id}`)}
                      className="text-xs text-[#8A6A22] underline"
                      data-testid={`open-doc-${i}`}
                    >
                      Open
                    </button>
                  )}
                  {r.entry_id && (
                    <button
                      onClick={() =>
                        openDownload(`/ledger/${r.entry_id}/receipt`).catch(() => toast.error("Could not open that"))
                      }
                      className="text-xs text-[#8A6A22] underline"
                      data-testid={`receipt-${i}`}
                    >
                      Receipt
                    </button>
                  )}
                  {r.receipt_file_id && (
                    <button
                      onClick={() =>
                        openDownload(`/files/${r.receipt_file_id}`).catch(() => toast.error("Could not open that"))
                      }
                      className="ml-2 text-xs text-[#8A6A22] underline"
                      data-testid={`proof-${i}`}
                      title="View payment proof"
                    >
                      <Paperclip className="inline h-3.5 w-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="mt-9 text-base font-bold uppercase tracking-wider md:text-lg">
        Records ({documents.length})
      </h2>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {documents
          .slice()
          .reverse()
          .map((d, i) => (
            <button
              key={d.id}
              onClick={() => navigate(`/documents/${d.id}`)}
              className="border border-border p-4 text-left transition-colors duration-150 hover:bg-[#FBF8F2]"
              data-testid={`customer-doc-${i}`}
            >
              <div className="flex items-baseline justify-between">
                <span className="font-semibold">#{d.doc_number}</span>
                <span className="tabular text-sm">{money(d.total)}</span>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {DOC_LABELS[d.doc_type]} · {d.doc_date} · {d.branch}
              </div>
              {(d.balance || 0) > 0.005 ? (
                <div className="mt-2 text-xs font-semibold text-[#9A3412]">
                  {money(d.balance)} outstanding
                </div>
              ) : (
                <div className="mt-2 text-xs font-semibold text-[#166534]">Settled</div>
              )}
            </button>
          ))}
      </div>

      {entryOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="entry-dialog"
        >
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto border border-border bg-white p-6">
            <h2 className="text-base font-bold md:text-lg">Take deposit / payment</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Leave the invoice blank to hold the money as credit on the account.
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Type
                </Label>
                <Select
                  value={entry.kind}
                  onValueChange={(v) => setEntry({ ...entry, kind: v })}
                >
                  <SelectTrigger className="mt-1.5 h-10 rounded-none" data-testid="entry-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-white">
                    <SelectItem value="deposit">Deposit received</SelectItem>
                    <SelectItem value="payment">Payment received</SelectItem>
                    <SelectItem value="refund">Refund paid out</SelectItem>
                    <SelectItem value="adjustment">Adjustment</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Amount (AUD)
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    className="mt-1.5 h-10 rounded-none text-right"
                    value={entry.amount}
                    onChange={(e) => setEntry({ ...entry, amount: e.target.value })}
                    data-testid="entry-amount"
                  />
                </div>
                <div>
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Method
                  </Label>
                  <Select
                    value={entry.method}
                    onValueChange={(v) => setEntry({ ...entry, method: v })}
                  >
                    <SelectTrigger className="mt-1.5 h-10 rounded-none" data-testid="entry-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-white">
                      {METHODS.map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Apply to invoice (optional)
                </Label>
                <Select
                  value={entry.doc_id || "none"}
                  onValueChange={(v) => setEntry({ ...entry, doc_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="mt-1.5 h-10 rounded-none" data-testid="entry-invoice">
                    <SelectValue placeholder="Hold as credit" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-white">
                    <SelectItem value="none">Hold as credit on account</SelectItem>
                    {openInvoices.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        #{d.doc_number} — {money(d.balance)} outstanding
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Reference (transfer / receipt number)
                </Label>
                <Input
                  className="mt-1.5 h-10 rounded-none"
                  value={entry.reference}
                  onChange={(e) => setEntry({ ...entry, reference: e.target.value })}
                  data-testid="entry-reference"
                />
              </div>

              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Note
                </Label>
                <Input
                  className="mt-1.5 h-10 rounded-none"
                  value={entry.note}
                  onChange={(e) => setEntry({ ...entry, note: e.target.value })}
                  data-testid="entry-note"
                />
              </div>

              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Payment screenshot / proof
                </Label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => uploadReceipt(e.target.files?.[0])}
                  className="mt-1.5 block w-full border border-input p-2 text-sm"
                  data-testid="entry-receipt-file"
                />
                {uploading && <p className="mt-1 text-xs text-muted-foreground">Uploading…</p>}
                {receiptName && (
                  <p className="mt-1 text-xs text-[#166534]" data-testid="receipt-attached">
                    Attached: {receiptName}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-6 flex gap-2">
              <Button
                variant="outline"
                className="flex-1 rounded-none"
                onClick={() => setEntryOpen(false)}
                data-testid="cancel-entry"
              >
                Cancel
              </Button>
              <Button
                disabled={saving}
                className="flex-1 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
                onClick={saveEntry}
                data-testid="save-entry"
              >
                Save &amp; print receipt
              </Button>
            </div>
          </div>
        </div>
      )}

      {editOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="edit-customer-dialog"
        >
          <div className="w-full max-w-md border border-border bg-white p-6">
            <h2 className="text-base font-bold md:text-lg">Customer details</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              The phone number {formatPhone(customer.phone)} and all history stay attached. A changed name is
              kept as a former name.
            </p>
            <div className="mt-5 space-y-3">
              {[
                ["name", "Name"],
                ["email", "Email"],
                ["address", "Address"],
                ["birthday", "Birthday"],
                ["notes", "Notes"],
              ].map(([key, label]) => (
                <div key={key}>
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {label}
                  </Label>
                  {key === "address" ? (
                    <AddressAutocomplete
                      className="mt-1.5 h-10 rounded-none"
                      value={profile.address}
                      onChange={(v) => setProfile({ ...profile, address: v })}
                      placeholder="Start typing the address"
                      testid="customer-address-input"
                    />
                  ) : (
                    <Input
                      className="mt-1.5 h-10 rounded-none"
                      value={profile[key]}
                      onChange={(e) => setProfile({ ...profile, [key]: e.target.value })}
                      data-testid={`customer-${key}-input`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-6 flex gap-2">
              <Button
                variant="outline"
                className="flex-1 rounded-none"
                onClick={() => setEditOpen(false)}
                data-testid="cancel-customer-edit"
              >
                Cancel
              </Button>
              <Button
                disabled={saving}
                className="flex-1 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
                onClick={saveProfile}
                data-testid="save-customer"
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {mergeOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="merge-dialog"
        >
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto border border-border bg-white p-6">
            <h2 className="text-base font-bold md:text-lg">Merge a duplicate customer</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Find the other record, check it is the same person, then merge. Everything moves
              onto <strong>this</strong> record and the other one is removed. This cannot be undone.
            </p>

            <div className="mt-5 flex gap-2">
              <Input
                className="h-10 rounded-none"
                placeholder="Other customer's phone number"
                inputMode="tel"
                value={mergeOther}
                onChange={(e) => {
                  setMergeOther(e.target.value);
                  setMergeMatch(null);
                }}
                data-testid="merge-other-phone"
              />
              <Button
                variant="outline"
                className="h-10 whitespace-nowrap rounded-none"
                onClick={findOther}
                data-testid="merge-find-button"
              >
                Find record
              </Button>
            </div>

            {mergeMatch && (
              <>
                <div className="mt-5 grid grid-cols-2 gap-3 text-sm" data-testid="merge-compare">
                  <div className="border border-[#166534]/40 bg-[#F0FDF4] p-3">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#166534]">
                      Keep — this record
                    </div>
                    <MergeRows
                      name={customer.name}
                      phone={customer.phone}
                      address={customer.address}
                      email={customer.email}
                      birthday={customer.birthday}
                    />
                  </div>
                  <div className="border border-[#9A3412]/40 bg-[#FDF7F4] p-3">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#9A3412]">
                      Remove — merged in
                    </div>
                    <MergeRows
                      name={mergeMatch.customer_name}
                      phone={mergeMatch.customer_phone}
                      address={mergeMatch.customer_address}
                      email={mergeMatch.customer_email}
                      purchases={mergeMatch.purchase_count}
                    />
                  </div>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Any details missing from the kept record are filled from the other one; the
                  other name and number are kept on file under “formerly”.
                </p>
                <div className="mt-4">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Reason (kept on the record)
                  </Label>
                  <Input
                    className="mt-1.5 h-10 rounded-none"
                    value={mergeReason}
                    onChange={(e) => setMergeReason(e.target.value)}
                    data-testid="merge-reason"
                  />
                </div>
              </>
            )}

            <div className="mt-6 flex gap-2">
              <Button
                variant="outline"
                className="flex-1 rounded-none"
                onClick={() => setMergeOpen(false)}
                data-testid="merge-cancel"
              >
                Cancel
              </Button>
              <Button
                disabled={!mergeMatch || !mergeReason.trim() || saving}
                className="flex-1 rounded-none bg-[#9A3412] font-semibold text-white hover:bg-[#7c2a0e]"
                onClick={doMerge}
                data-testid="merge-confirm"
              >
                Merge records
              </Button>
            </div>
          </div>
        </div>
      )}

      <CollectKycDialog
        open={collectOpen}
        onClose={() => setCollectOpen(false)}
        customer={{
          name: customer.name,
          phone: customer.phone,
          email: customer.email,
          address: customer.address,
        }}
      />
    </div>
  );
}

const MergeRows = ({ name, phone, address, email, birthday, purchases }) => (
  <div className="mt-1.5 space-y-0.5">
    <div className="font-semibold">{name || "—"}</div>
    <div className="tabular text-muted-foreground">{formatPhone(phone) || "—"}</div>
    <div className="text-muted-foreground">{address || "—"}</div>
    <div className="text-muted-foreground">{email || "—"}</div>
    {birthday ? <div className="text-muted-foreground">{birthday}</div> : null}
    {typeof purchases === "number" ? (
      <div className="text-xs text-muted-foreground">{purchases} purchase(s) on file</div>
    ) : null}
  </div>
);

const Stat = ({ label, value, testid, tone }) => (
  <div
    className={`border p-4 ${
      tone === "owing"
        ? "border-[#9A3412]/40 bg-[#FDF7F4]"
        : tone === "credit"
        ? "border-[#166534]/40 bg-[#F0FDF4]"
        : "border-border"
    }`}
    data-testid={testid}
  >
    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </div>
    <div className="tabular mt-1.5 text-2xl font-bold">{value}</div>
  </div>
);
